import BaseAPIService from '../base/base-api-service';
import * as moment from 'moment';
import InvoiceModel from 'src/model/invoice.model';
import {
  sortType,
  statusType,
  orderType,
  invoiceType,
  nardType,
  ordernumberType,
  poType
} from 'src/model/modules/invoices.model';
import DateRangeModel from 'src/model/dateRange.model';

export interface IFiltersInvoice {
  dateRange: DateRangeModel;
  sort: { type: sortType; order: 'asc' | 'desc' };
  status: statusType;
  orderStatus: orderType;
  // search: searchType;
  invoiceNumber: invoiceType;
  nardaNumber: nardType;
  orderNumber: ordernumberType;
  orderReference: poType;
}

export interface IPaginationInvoice {
  page: number;
  size?: number;
}

export interface IInvoiceCreditPayment {
  orderNumber: number;
  paymentAmount: number;
  reasonRemainingBalance: string;
  transactionId: number;
}

export interface IInvoiceInitialModel {
  customerNumber: string;
  description: string;
  dueDate: string;
  hasInvoice: boolean;
  invoiceDate: string;
  invoiceNumber: number;
  orderNumber: number;
  remainingAmount: number;
  status: 'PAID' | 'DUE' | 'PAST DUE' | 'PENDING';
  orderStatus: 'RS' | 'RW' | 'NC' | 'CR' | 'IN';
  // invoiceNumber: 'invoiceNumber' ;
  nardaNumber: 'nardaNumber';
  transactionId: number;
}

class InvoiceService extends BaseAPIService<InvoiceModel> {
  private endpoint: string = 'accountapp/v1/invoice';

  constructor() {
    super(InvoiceModel);
  }

  private buildInvoicesParams = (
    filters: IFiltersInvoice,
    pagination: IPaginationInvoice,
    isHeader?: boolean
  ) => {
    const parameters: any = {
      dateFrom:
        isHeader || pagination.size === 0 || pagination.size
          ? undefined
          : filters.dateRange && filters.dateRange.start
          ? filters.dateRange.start.format('YYYY-MM-DD')
          : undefined,
      dateTo:
        isHeader || pagination.size === 0 || pagination.size
          ? undefined
          : filters.dateRange && filters.dateRange.end
          ? filters.dateRange.end.format('YYYY-MM-DD')
          : undefined,
      page: pagination.page,
      size: pagination.size,
      sort:
        filters.sort && filters.sort.type && filters.sort.order
          ? `${filters.sort.type},${filters.sort.order}`
          : 'invoiceDate,asc',
      status: filters.status,
      orderStatus: filters.orderStatus,
      invoiceNumber: filters.invoiceNumber,
      nardaNumber: filters.nardaNumber,
      orderNumber: filters.orderNumber,
      orderReference: filters.orderReference
    };
    if (isHeader || pagination.size === 0 || pagination.size) {
      parameters['dueDateFrom'] =
        filters.dateRange && filters.dateRange.start
          ? filters.dateRange.start.format('YYYY-MM-DD')
          : undefined;
      parameters['dueDateTo'] =
        filters.dateRange && filters.dateRange.end
          ? filters.dateRange.end.format('YYYY-MM-DD')
          : undefined;
    }
    Object.keys(parameters).forEach(key =>
      !parameters[key] ? delete parameters[key] : ''
    );
    return parameters;
  };

  private buildOldestInvoicesParams = (pagination: IPaginationInvoice) => {
    console.log(pagination);
    const parameters: any = {
      page: pagination.page,
      size: pagination.size
    };

    return parameters;
  };

  private buildCreditParams = (
    filters: any,
    pagination: IPaginationInvoice
  ) => {
    console.log(pagination);
    const parameters: any = {
      page: pagination.page,
      size: pagination.size,
      invoiceNumber: filters.invoiceNumber
    };
    Object.keys(parameters).forEach(key =>
      !parameters[key] ? delete parameters[key] : ''
    );
    return parameters;
  };

  private setLocalIndex(invoices: IInvoiceInitialModel[]) {
    return invoices.map((invoice, idx) => {
      return {
        ...invoice,
        idx: `${invoice.transactionId}&${invoice.orderNumber}`
      };
    });
  }

  public getInvoices(
    filters: IFiltersInvoice,
    pagination: IPaginationInvoice,
    isHeader?: boolean
  ) {
    const params = this.buildInvoicesParams(filters, pagination, isHeader);
    return new Promise((resolve, reject) => {
      this.getOneByParams(`${this.baseURL}${this.endpoint}`, params)
        .then((response: any) => {
          const data = this.setLocalIndex(response.data);
          const result = { ...response, data };
          if (result.content) {
            resolve(result);
          } else {
            const newResponse = { ...result };
            newResponse.content = [];
            resolve(newResponse);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public sendInvoiceByEmail(addresses: string[], invoicesNumbers: any[]) {
    return new Promise((resolve, reject) => {
      const emailEndpoint = `${this.baseURL}accountapp/v1/invoice/send/email`;
      this.doRequest({
        method: 'post',
        url: emailEndpoint,
        data: {
          emailsToSend: addresses,
          invoicesNumber: invoicesNumbers
        }
      })
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public getPaymentHistory(accountId: any, filters: any) {
    const params = this.buildPaymentHistoryParams(accountId, filters);
    return new Promise((resolve, reject) => {
      const paymentEndpoint = `${this.baseURL}accountapp/v1/payments/history`;
      this.doRequest({
        method: 'post',
        url: paymentEndpoint,
        data: params
        // data: {
        //   accountId,
        //   fromDate: '',
        //   invoiceNo: '',
        //   orderNo: '',
        //   purchaseOrderdNo: '',
        //   status: '',
        //   toDate: ''
        // }
      })
        .then((response: any) => {
          resolve(response.data);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  private buildPaymentHistoryParams = (accountId: any, filters: any) => {
    const start = moment(
      new Date(new Date().setFullYear(new Date().getFullYear() - 5))
    );
    const end = moment(new Date());
    if (filters) {
      const parameters: any = {
        accountId,
        fromDate:
          filters.dateRange && filters.dateRange.start
            ? filters.dateRange.start.format('MMDDYYYY')
            : start.format('MMDDYYYY'),
        toDate:
          filters.dateRange && filters.dateRange.end
            ? filters.dateRange.end.format('MMDDYYYY')
            : end.format('MMDDYYYY'),
        status: '',
        // orderStatus: filters.orderStatus,
        invoiceNo: filters.invoiceNumber ? filters.invoiceNumber : '',
        orderNo: filters.orderNumber ? filters.orderNumber : '',
        purchaseOrderdNo: filters.orderReference ? filters.orderReference : ''
      };
      return parameters;
    } else {
      const parameters: any = {
        accountId,
        fromDate: start.format('MMDDYYYY'),
        invoiceNo: '',
        orderNo: '',
        purchaseOrderdNo: '',
        status: '',
        toDate: end.format('MMDDYYYY')
      };
      return parameters;
    }
  };
  public originalInvoice() {
    return new Promise((resolve, reject) => {
      const originalInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/original/invoice`;
      this.doRequest({
        method: 'get',
        url: originalInvoiceEndpoint
      })
        .then((response: any) => {
          // const data = this.setLocalIndex(response.data);
          const result = response.data;
          if (response.status === 200) {
            resolve(result);
          } else if (response.status === 204) {
            reject('No original invoices');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
  public oldestInvoice(pagination: IPaginationInvoice) {
    const params = this.buildOldestInvoicesParams(pagination);
    return new Promise((resolve, reject) => {
      const oldestInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/oldest/invoice`;
      this.doRequest({
        method: 'get',
        url: oldestInvoiceEndpoint,
        params
      })
        .then((response: any) => {
          // const data = this.setLocalIndex(response.data);
          const result = response.data;
          console.log(
            'olderst invoice:' + JSON.stringify(response.totalCredit)
          );
          if (response.status === 200) {
            resolve(result);
          } else if (response.status === 204) {
            reject('No oldest invoices');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public creditDebitSummary(type: any) {
    return new Promise((resolve, reject) => {
      const summaryEndpoint = `${this.baseURL}accountapp/v1/invoice/credit/debit/summary?type=${type}`;
      this.doRequest({
        method: 'get',
        url: summaryEndpoint
      })
        .then((response: any) => {
          // const data = this.setLocalIndex(response.data);
          const result = response.data;
          if (response.status === 200) {
            resolve(result);
          } else if (response.status === 204) {
            reject('No Summary');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
  public selectCreditOriginal(filters: any, pagination: any) {
    const params = this.buildOriginalCreditParams(filters, pagination);
    console.log(params);
    const manualcreditInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/select/credit`;
    return new Promise((resolve, reject) => {
      this.getOneByParams(`${manualcreditInvoiceEndpoint}`, params)
        .then((response: any) => {
          const data = this.setLocalIndex(response.data);
          const result = { ...response, data };
          if (result.content) {
            resolve(result);
          } else {
            const newResponse = { ...result };
            newResponse.content = [];
            resolve(newResponse);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }
  private buildOriginalCreditParams = (
    filters: any,
    pagination: IPaginationInvoice
  ) => {
    console.log(pagination);
    const parameters: any = {
      page: pagination.page,
      size: pagination.size,
      listOfOriginalInvoices: filters.listOfOriginalInvoices,
      type: 'original'
    };
    Object.keys(parameters).forEach(key =>
      !parameters[key] ? delete parameters[key] : ''
    );
    return parameters;
  };
  public selectCredit(filters: any, pagination: any) {
    const params = this.buildCreditParams(filters, pagination);
    console.log(params);
    const manualcreditInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/select/credit`;
    return new Promise((resolve, reject) => {
      this.getOneByParams(`${manualcreditInvoiceEndpoint}`, params)
        .then((response: any) => {
          const data = this.setLocalIndex(response.data);
          const result = { ...response, data };
          if (result.content) {
            resolve(result);
          } else {
            const newResponse = { ...result };
            newResponse.content = [];
            resolve(newResponse);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public exportSelectedInvoicesSummary(
    type: string,
    paymentId: string,
    debitDataList: any,
    creditDataList: any,
    page: string
  ) {
    return new Promise((resolve, reject) => {
      let exportInvoicesEndpoint = '';
      if (page === 'admin') {
        exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}&summary=admin`;
      } else if (page === 'original') {
        exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}&summary=original`;
      } else {
        exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}`;
      }
      // page === 'manual'
      //   ? `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}`
      //   :
      // page === 'admin'?
      // `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}&summary=admin`
      // : page === 'original'
      //   ? `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}&summary=original`
      //   : `${this.baseURL}accountapp/v1/invoice/selected/cranddr/export?type=${type}&payment_id=${paymentId}`;
      this.doRequest({
        method: 'post',
        url: exportInvoicesEndpoint,
        data: {
          creditInvoice: [],
          debitInvoice: []
        },
        responseType: 'blob'
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public exportSelectedInvoices(type: string, selectedInvoice: any) {
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/selected/export?type=${type}`;
      this.doRequest({
        method: 'post',
        url: exportInvoicesEndpoint,
        data: selectedInvoice,
        responseType: 'blob'
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public exportSelectedPaidInvoices(type: string, selectedInvoice: any) {
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/payments/history/selected/export?type=${type}`;
      this.doRequest({
        method: 'post',
        url: exportInvoicesEndpoint,
        data: selectedInvoice,
        responseType: 'blob'
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public downloadAll(
    filename: string,
    type: string,
    filtervalues: IFiltersInvoice,
    pagination: IPaginationInvoice
  ) {
    // const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/export?type=${type}`;
    let exportParams: any;
    if (filtervalues !== undefined) {
      exportParams = this.buildInvoicesParams(filtervalues, pagination);
    }
    if (filtervalues === undefined && pagination) {
      exportParams = {
        page: pagination.page,
        size: pagination.size,
        sort: 'invoiceDate,asc'
      };
    }
    // const tempLink = document.createElement('a');
    // tempLink.style.display = 'none';
    // tempLink.href = exportInvoicesEndpoint;
    // tempLink.setAttribute('download', filename);

    // // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // // target if the browser does not support the HTML5 download attribute.
    // // This allows you to download files in desktop safari if pop up blocking
    // // is enabled.
    // if (typeof tempLink.download === 'undefined') {
    //   tempLink.setAttribute('target', '_blank');
    // }

    // document.body.appendChild(tempLink);
    // tempLink.click();
    // document.body.removeChild(tempLink);

    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/export?type=${type}`;
      this.doRequest({
        method: 'get',
        url: exportInvoicesEndpoint,
        responseType: 'blob',
        params: exportParams
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public exportInvoices(
    type: string,
    filtervalues: IFiltersInvoice,
    pagination: IPaginationInvoice
  ) {
    let exportParams: any;
    if (filtervalues !== undefined) {
      exportParams = this.buildInvoicesParams(filtervalues, pagination);
    }
    if (filtervalues === undefined && pagination) {
      exportParams = {
        page: pagination.page,
        size: pagination.size,
        sort: 'invoiceDate,asc'
      };
    }
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/export?type=${type}`;
      this.doRequest({
        method: 'get',
        url: exportInvoicesEndpoint,
        responseType: 'blob',
        params: exportParams
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public exportPaymentHistoryInvoices(
    type: string,
    filtervalues: IFiltersInvoice,
    pagination: IPaginationInvoice
  ) {
    // let exportParams: any;
    // if (filtervalues !== undefined) {
    //   exportParams = this.buildInvoicesParams(filtervalues, pagination);
    // }
    const activeAccount = localStorage.getItem('activeAccount');
    const params = this.buildPaymentHistoryParams(activeAccount, filtervalues);
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/payments/history/export?type=${type}`;
      this.doRequest({
        method: 'post',
        url: exportInvoicesEndpoint,
        responseType: 'blob',
        data: params
        // params: exportParams,
        // data: {
        //   accountId: '608209',
        //   fromDate: '',
        //   invoiceNo: '',
        //   orderNo: '',
        //   purchaseOrderdNo: '',
        //   status: '',
        //   toDate: ''
        // }
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  public exportOriginalInvoices(
    type: string,
    filtervalues: IFiltersInvoice,
    pagination: IPaginationInvoice
  ) {
    let exportParams: any;
    if (filtervalues !== undefined) {
      exportParams = this.buildInvoicesParams(filtervalues, pagination);
    }
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/original/export?type=${type}`;
      this.doRequest({
        method: 'get',
        url: exportInvoicesEndpoint,
        responseType: 'blob',
        params: exportParams
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
  public exportOldestInvoices(
    type: string,
    filtervalues: IFiltersInvoice,
    pagination: IPaginationInvoice
  ) {
    let exportParams: any;
    if (filtervalues !== undefined) {
      exportParams = this.buildInvoicesParams(filtervalues, pagination);
    }
    return new Promise((resolve, reject) => {
      const exportInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/oldest/export?type=${type}`;
      this.doRequest({
        method: 'get',
        url: exportInvoicesEndpoint,
        responseType: 'blob',
        params: exportParams
      })
        .then((response: any) => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices to download');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
  public downloadPaymentReference(paymentReferences: any[]) {
    return new Promise((resolve, reject) => {
      const downloadInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/download-payment-receipts?`;
      this.doRequest({
        method: 'get',
        // url: downloadInvoicesEndpoint,
        // params:paymentReferences,
        url: this.buildDownloadPaymentReferenceUrl(
          paymentReferences,
          downloadInvoicesEndpoint
        ),

        responseType: 'blob'
      })
        .then((response: any) => {
          if (response.status === 200 || response.status === 206) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
  private buildDownloadPaymentReferenceUrl(
    paymentReferences: any[],
    url: string
  ) {
    let newUrl = url;
    paymentReferences.map((payment, index) => {
      newUrl +=
        index === 0
          ? `number=${payment.paymentReference}:${payment.transactionId}`
          : `&number=${payment.paymentReference}:${payment.transactionId}`;
    });

    return newUrl;
  }

  public downloadInvoices(invoicesNumbers: any[]) {
    return new Promise((resolve, reject) => {
      const downloadInvoicesEndpoint = `${this.baseURL}accountapp/v1/invoice/download?`;
      this.doRequest({
        method: 'get',
        url: this.buildDownloadInvoicesUrl(
          invoicesNumbers,

          downloadInvoicesEndpoint
        ),

        responseType: 'blob'
      })
        .then((response: any) => {
          if (response.status === 200 || response.status === 206) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoices');
          } else {
            reject();
          }
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  private buildDownloadInvoicesUrl(invoicesNumbers: any[], url: string) {
    console.log('buildDownloadInvoicesUrl', invoicesNumbers);
    let newUrl = url;
    invoicesNumbers.map((invoice, index) => {
      console.log('invoiceNo', invoice.invoiceNo);
      newUrl +=
        index === 0
          ? `number=${invoice.invoiceNo}:${invoice.invoiceDate}`
          : `&number=${invoice.invoiceNo}:${invoice.invoiceDate}`;
    });

    return newUrl;
  }

  public downloadInvoice(invoice: string, date: string) {
    return new Promise((resolve, reject) => {
      const downloadInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/preview?number=${invoice}:${date}`;
      this.doRequest({
        method: 'get',
        url: downloadInvoiceEndpoint,
        responseType: 'blob'
      })
        .then(response => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoice');
          } else {
            reject();
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }
  public payInvoicesByCreditsOriginal(
    invoicePayments: IInvoiceCreditPayment[],
    amountDetails: any
  ) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/pay/credits?type=original`,
        data: {
          invoicePayments,
          ...amountDetails
        }
      })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public payInvoicesByCredits(
    invoicePayments: IInvoiceCreditPayment[],
    amountDetails: any
  ) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/pay/credits`,
        data: {
          invoicePayments,
          ...amountDetails
        }
      })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }
  public manualApplyCredits(
    invoicePayments: IInvoiceCreditPayment[],
    amountDetails: any
  ) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/pay/manual/credits`,
        data: {
          invoicePayments,
          ...amountDetails
        }
      })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public manualyAutoApplyCredits(invoiceDatas: any) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/pay/auto/credits`,
        data: invoiceDatas
      })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public allowUserModifyInvoices(accountId: string, clientIdentity: string) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/permission/enable`,
        data: {
          accountId,
          clientIdentity
        }
      })
        .then(response => {
          if (response.status === 200) {
            resolve(response);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public disableUserModifyInvoices(accountId: string, clientIdentity: string) {
    return new Promise((resolve, reject) => {
      this.doRequest({
        method: 'POST',
        url: `${this.baseURL}accountapp/v1/invoice/permission/disable`,
        data: {
          accountId,
          clientIdentity
        }
      })
        .then(response => {
          if (response.status === 200) {
            resolve(response);
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public downloadInvoiceByOrderno(invoice: string, orderno: string) {
    return new Promise((resolve, reject) => {
      const downloadInvoiceEndpoint = `${this.baseURL}accountapp/v1/invoice/previewByOrderNumber?number=${invoice}&orderNumber=${orderno}`;
      this.doRequest({
        method: 'get',
        url: downloadInvoiceEndpoint,
        responseType: 'blob'
      })
        .then(response => {
          if (response.status === 200) {
            resolve(response);
          } else if (response.status === 204) {
            reject('No invoice');
          } else {
            reject();
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  }
}

const invoiceServiceInstance = new InvoiceService();
Object.freeze(invoiceServiceInstance);

export default invoiceServiceInstance;
